import React, { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import FeatureGate from "../components/FeatureGate";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Brain, Sparkles, Leaf, Cable, TrendingUp, Check, X, ArrowUpRight, ArrowDownRight, Wand2, Save } from "lucide-react";

const IMPACT = { high: "success", medium: "warning", low: "outline" };
const CONN = { connected: "success", pending: "warning" };

/* ---------------- AI Scope 3 Mapper ---------------- */
function MapperTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/scope3-intel/mapper/queue").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const classify = async (pid) => {
    setBusy(pid);
    try { await api.post(`/scope3-intel/mapper/classify/${pid}`); toast.success("AI classification ready"); load(); }
    catch (e) { toast.error(apiErr(e, "Classification failed")); } finally { setBusy(null); }
  };
  const review = async (pid, action) => {
    try { await api.post(`/scope3-intel/mapper/${pid}/review`, { action }); toast.success(`Suggestion ${action}ed`); load(); }
    catch (e) { toast.error("Review failed"); }
  };

  return (
    <div className="space-y-3" data-testid="intel-mapper">
      <div className="card-flat p-3 text-[11px] text-muted-foreground">AI suggestions are <strong className="text-foreground">advisory</strong> — a human accepts or rejects each one; nothing auto-enters the GHG ledger.</div>
      {items.map((it) => (
        <div key={it.product_id} className="card-flat p-4" data-testid={`mapper-${it.product_id}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="font-display font-bold text-sm">{it.name}</div>
              <div className="text-[10px] font-mono text-muted-foreground">{it.supplier_name} · {it.geography} · {it.co2e} tCO₂e · tier {it.method_tier || "—"}{it.cbam_relevant ? " · CBAM" : ""}</div>
            </div>
            {!it.suggestion && (
              <Button size="sm" variant="primary" onClick={() => classify(it.product_id)} disabled={busy === it.product_id} data-testid={`mapper-classify-${it.product_id}`}>
                <Wand2 className="h-3.5 w-3.5" /> {busy === it.product_id ? "Classifying…" : "Classify with AI"}
              </Button>
            )}
          </div>
          {it.suggestion && (
            <div className="mt-3 pt-3 border-t border-border" data-testid={`mapper-suggestion-${it.product_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-[9px]"><Sparkles className="h-3 w-3 mr-1 text-accent" /> Cat {it.suggestion.category_id}: {it.suggestion.category_name}</Badge>
                <Badge variant="outline" className="text-[9px]">confidence {it.suggestion.confidence}%</Badge>
                <Badge variant="outline" className="text-[9px]">method {it.suggestion.suggested_method_tier}</Badge>
                <Badge variant={it.suggestion.status === "accepted" ? "success" : it.suggestion.status === "rejected" ? "destructive" : "warning"} className="text-[8px] uppercase">{it.suggestion.status}</Badge>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1.5">{it.suggestion.rationale}</div>
              {it.suggestion.status === "suggested" && (
                <div className="flex gap-1.5 mt-2">
                  <Button size="sm" variant="primary" onClick={() => review(it.product_id, "accept")} data-testid={`mapper-accept-${it.product_id}`}><Check className="h-3.5 w-3.5" /> Accept</Button>
                  <Button size="sm" variant="ghost" onClick={() => review(it.product_id, "reject")} data-testid={`mapper-reject-${it.product_id}`}><X className="h-3.5 w-3.5" /> Reject</Button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      {!items.length && <div className="card-flat p-8 text-center text-muted-foreground text-sm">No products to classify.</div>}
    </div>
  );
}

/* ---------------- Decarbonisation Engine ---------------- */
function DecarbTab() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/scope3-intel/decarb/ranking").then((r) => setData(r.data)); }, []);

  const draftPlan = async (sid) => {
    setBusy(sid);
    try { const { data } = await api.post(`/scope3-intel/decarb/draft-plan/${sid}`); setDraft(data); }
    catch (e) { toast.error(apiErr(e, "Draft failed")); } finally { setBusy(null); }
  };
  const savePlan = async () => {
    try {
      await api.post("/supply-chain/action-plans", {
        supplier_id: draft.supplier_id, supplier_name: draft.supplier_name, title: draft.title,
        issue: draft.rationale, target_tier: draft.target_tier,
        actions: draft.actions.map((a) => ({ text: a.text, owner: a.owner, status: "open" })),
      });
      toast.success("Saved to Action Plans"); setDraft(null);
    } catch (e) { toast.error("Save failed"); }
  };

  if (!data) return <div className="p-6 text-muted-foreground font-mono text-sm">Loading opportunities…</div>;
  return (
    <div className="space-y-3" data-testid="intel-decarb">
      <div className="card-flat p-4 flex items-center justify-between flex-wrap gap-2">
        <div><div className="label-eyebrow text-[10px]">Total addressable reduction</div><div className="font-display text-3xl font-bold">{data.total_estimated_reduction_tco2e} <span className="text-sm text-muted-foreground">tCO₂e</span></div></div>
        <div className="text-[10px] text-muted-foreground max-w-xs">{data.note}</div>
      </div>
      {data.opportunities.map((o, i) => (
        <div key={o.supplier_id} className="card-flat p-4" data-testid={`decarb-${o.supplier_id}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-mono text-lg font-bold w-6 text-muted-foreground">#{i + 1}</div>
            <div className="flex-1 min-w-[180px]">
              <div className="font-display font-bold text-sm">{o.supplier_name}</div>
              <div className="text-[10px] font-mono text-muted-foreground">{o.footprint_tco2e} tCO₂e · tier {o.best_method_tier || "—"} · {o.reduction_headroom_pct}% headroom</div>
            </div>
            <div className="text-right">
              <div className="text-[9px] text-muted-foreground uppercase">Est. reduction</div>
              <div className="font-display text-xl font-bold text-[#00E676]">−{o.estimated_reduction_tco2e}</div>
            </div>
            <Button size="sm" variant="primary" onClick={() => draftPlan(o.supplier_id)} disabled={busy === o.supplier_id} data-testid={`decarb-draft-${o.supplier_id}`}>
              <Sparkles className="h-3.5 w-3.5" /> {busy === o.supplier_id ? "Drafting…" : "Draft plan"}
            </Button>
          </div>
          <div className="flex gap-1.5 flex-wrap mt-2">{o.levers.map((l, j) => <Badge key={j} variant="outline" className="text-[8px]">{l}</Badge>)}</div>
        </div>
      ))}

      {draft && (
        <div className="card-flat p-5 border-accent/40" data-testid="decarb-draft-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="label-eyebrow flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" /> {draft.title}</div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="primary" onClick={savePlan} data-testid="decarb-save-plan"><Save className="h-3.5 w-3.5" /> Save as action plan</Button>
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)}><X className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">{draft.rationale} · target tier {draft.target_tier} · AI-drafted (Claude Sonnet 5)</div>
          <div className="space-y-1.5 mt-3">
            {draft.actions.map((a, i) => (
              <div key={i} className="flex items-start gap-2 p-2 rounded-sm bg-surface" data-testid={`draft-action-${i}`}>
                <Leaf className="h-4 w-4 text-[#00E676] shrink-0 mt-0.5" />
                <div className="flex-1"><div className="text-xs">{a.text}</div><div className="text-[9px] font-mono text-muted-foreground">{a.owner}</div></div>
                <Badge variant={IMPACT[a.impact] || "outline"} className="text-[7px] uppercase">{a.impact}</Badge>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-muted-foreground mt-2 italic">{draft.note}</div>
        </div>
      )}
    </div>
  );
}

/* ---------------- PACT Exchange ---------------- */
function PactTab() {
  const [conns, setConns] = useState([]);
  const [ex, setEx] = useState([]);
  const load = () => {
    api.get("/scope3-intel/pact/connections").then((r) => setConns(r.data.connections || []));
    api.get("/scope3-intel/pact/exchanges").then((r) => setEx(r.data.exchanges || []));
  };
  useEffect(() => { load(); }, []);

  const request = async (cid) => {
    try { await api.post("/scope3-intel/pact/request", { connection_id: cid, product_name: "Requested PCF" }); toast.success("PCF request sent"); load(); }
    catch (e) { toast.error("Request failed"); }
  };
  const receive = async () => {
    try {
      await api.post("/scope3-intel/pact/receive", {
        connection_id: "pact-agrihub", product_name: "Peat-free growing media", supplier_name: "AgriChem PACT Hub",
        pcf: { pCfExcludingBiogenic: 1.8, declaredUnit: "kg", boundary: "cradle-to-gate", verified: true, geographyCountry: "Malaysia", primaryDataShare: 85 },
      });
      toast.success("Inbound PCF received as a draft"); load();
    } catch (e) { toast.error("Receive failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="intel-pact">
      <div className="card-flat p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow flex items-center gap-2"><Cable className="h-4 w-4 text-accent" /> Partner connections</div>
          <Button size="sm" variant="outline" onClick={receive} data-testid="pact-receive-btn">Simulate inbound PCF</Button>
        </div>
        <div className="space-y-2">
          {conns.map((c) => (
            <div key={c.id} className="p-3 rounded-sm border border-border bg-surface" data-testid={`pact-conn-${c.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div><div className="text-sm font-medium">{c.partner_name}</div><div className="text-[9px] font-mono text-muted-foreground">{c.partner_id || "—"} · PACT v{c.pact_version}</div></div>
                <div className="flex items-center gap-2">
                  <Badge variant={CONN[c.status] || "outline"} className="text-[8px] uppercase">{c.status}</Badge>
                  {c.status === "connected" && <Button size="sm" variant="ghost" onClick={() => request(c.id)} data-testid={`pact-request-${c.id}`}>Request PCF</Button>}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-[10px] text-muted-foreground mt-3 italic">Conformant PACT V3 exchange layer — ready to connect to a real partner endpoint when credentials are provided.</div>
      </div>
      <div className="card-flat p-5">
        <div className="label-eyebrow mb-3">Exchange log</div>
        <div className="space-y-2">
          {ex.map((e) => (
            <div key={e.id} className="flex items-center gap-2 p-2.5 rounded-sm border border-border bg-surface text-[11px]" data-testid={`pact-ex-${e.id}`}>
              {e.direction === "inbound" ? <ArrowDownRight className="h-4 w-4 text-[#00E676]" /> : <ArrowUpRight className="h-4 w-4 text-[#FFB800]" />}
              <div className="flex-1"><span className="font-medium">{e.product_name}</span> <span className="text-muted-foreground">· {e.partner}</span></div>
              <Badge variant="outline" className="text-[7px] uppercase">{e.direction} · {e.status}</Badge>
            </div>
          ))}
          {!ex.length && <div className="text-muted-foreground text-sm py-6 text-center">No exchanges yet.</div>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Supplier KPI Suite ---------------- */
function Spark({ points, invert }) {
  if (!points || points.length < 2) return null;
  const vals = points;
  const min = Math.min(...vals), max = Math.max(...vals), range = max - min || 1;
  const w = 120, h = 32;
  const d = vals.map((v, i) => `${(i / (vals.length - 1)) * w},${h - ((v - min) / range) * h}`).join(" ");
  const up = vals[vals.length - 1] >= vals[0];
  const good = invert ? !up : up;
  return <svg width={w} height={h} className="overflow-visible"><polyline points={d} fill="none" stroke={good ? "#00E676" : "#FF5252"} strokeWidth="2" /></svg>;
}

function KpiTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/scope3-intel/kpis/trends").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-6 text-muted-foreground font-mono text-sm">Loading KPI trends…</div>;
  const roll = data.portfolio;
  const metricRow = (key, label, invert) => (
    <div className="flex items-center gap-3 flex-wrap py-2 border-b border-border last:border-0">
      <div className="w-40 text-xs font-medium">{label}</div>
      <Spark points={roll.map((r) => r[key])} invert={invert} />
      <div className="font-mono text-sm">{roll[0]?.[key]} <span className="text-muted-foreground">→</span> <span className="font-bold">{roll[roll.length - 1]?.[key]}</span></div>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="intel-kpis">
      <div className="card-flat p-5">
        <div className="label-eyebrow mb-2 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-accent" /> Portfolio trend · {data.periods.join(" → ")}</div>
        {metricRow("carbon_intensity", "Carbon intensity (↓ better)", true)}
        {metricRow("data_confidence", "Data confidence (↑ better)", false)}
        {metricRow("engagement", "Engagement (↑ better)", false)}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.suppliers.map((s) => {
          const pts = s.points;
          return (
            <div key={s.supplier_name} className="card-flat p-4" data-testid={`kpi-${s.supplier_name.replace(/\s+/g, "-")}`}>
              <div className="font-display font-bold text-sm mb-2">{s.supplier_name}</div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><div className="text-[9px] text-muted-foreground uppercase">Carbon</div><Spark points={pts.map((p) => p.carbon_intensity)} invert /><div className="font-mono text-xs font-bold">{pts[pts.length - 1].carbon_intensity}</div></div>
                <div><div className="text-[9px] text-muted-foreground uppercase">Confidence</div><Spark points={pts.map((p) => p.data_confidence)} /><div className="font-mono text-xs font-bold">{pts[pts.length - 1].data_confidence}</div></div>
                <div><div className="text-[9px] text-muted-foreground uppercase">Engagement</div><Spark points={pts.map((p) => p.engagement)} /><div className="font-mono text-xs font-bold">{pts[pts.length - 1].engagement}</div></div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="card-flat p-3 text-[10px] text-muted-foreground">{data.metrics.carbon_intensity} · {data.metrics.data_confidence} · {data.metrics.engagement}</div>
    </div>
  );
}

export default function Scope3Intelligence() {
  return (
    <FeatureGate featureKey="scope3_intelligence">
      <div className="p-6 space-y-5" data-testid="scope3-intel-page">
        <div>
          <div className="label-eyebrow mb-1 flex items-center gap-2">Supply Chain <Badge variant="outline" className="text-[8px]"><Sparkles className="h-2.5 w-2.5 mr-0.5 text-accent" /> Premium add-on</Badge></div>
          <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Brain className="h-6 w-6 text-accent" /> Scope 3 Intelligence & Exchange</h2>
          <p className="text-muted-foreground text-sm mt-1 max-w-3xl">AI Scope 3 mapping, the Supplier Decarbonisation Opportunity Engine, PACT V3 machine-to-machine exchange, and time-series supplier KPI trends. AI is advisory — humans decide before anything enters the GHG ledger.</p>
        </div>
        <Tabs defaultValue="mapper">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="mapper" data-testid="intel-tab-mapper">AI Scope 3 Mapper</TabsTrigger>
            <TabsTrigger value="decarb" data-testid="intel-tab-decarb">Decarbonisation Engine</TabsTrigger>
            <TabsTrigger value="pact" data-testid="intel-tab-pact">PACT Exchange</TabsTrigger>
            <TabsTrigger value="kpis" data-testid="intel-tab-kpis">KPI Trends</TabsTrigger>
          </TabsList>
          <TabsContent value="mapper" className="mt-4"><MapperTab /></TabsContent>
          <TabsContent value="decarb" className="mt-4"><DecarbTab /></TabsContent>
          <TabsContent value="pact" className="mt-4"><PactTab /></TabsContent>
          <TabsContent value="kpis" className="mt-4"><KpiTab /></TabsContent>
        </Tabs>
      </div>
    </FeatureGate>
  );
}
